/**
 * Devices Controller Module
 * Handles Devices page initialization, data loading, and filtering
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only devices page logic
 * - Template Method: Uses BaseController hooks
 * - Sprint 29: Added pagination support using reusable pagination utils
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import {
    PaginationState,
    PaginationElements,
    createPaginationState,
    updatePaginationUI,
    resetPagination,
    getPageItems,
    setupPaginationListeners
} from '../utils/pagination-utils';

/**
 * Device data structure
 */
interface Device {
    sys_id: string;
    device_id?: string;
    hostname?: string;
    ip?: string;
    device_type?: string;
    os?: string;
    os_version?: string;
    status?: string;
    [key: string]: any;
}

/**
 * DevicesController class
 * Manages the devices page with data loading and filtering
 */
export class DevicesController extends BaseController {
    private allDevices: Device[] = [];
    private filteredDevices: Device[] = [];

    // Sprint 29: Pagination state
    private paginationState: PaginationState = createPaginationState(10);
    private paginationElements: PaginationElements = {
        infoId: 'devices-pagination-info',
        prevButtonId: 'devices-prev-page',
        nextButtonId: 'devices-next-page'
    };

    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'devices';
    }

    protected async doInit(): Promise<void> {
        await this.loadDevicesData();
    }

    protected setupEventListeners(): void {
        this.setupDeviceSearchAndFilters();
        this.setupPagination();
    }

    /**
     * Setup pagination event listeners
     */
    private setupPagination(): void {
        setupPaginationListeners(
            this.paginationElements,
            () => this.renderCurrentPage(),
            () => this.paginationState,
            (state) => { this.paginationState = state; }
        );
    }

    /**
     * Render current page of devices
     */
    private renderCurrentPage(): void {
        const pageDevices = getPageItems(this.filteredDevices, this.paginationState);
        this.renderDevicesTable(pageDevices);
        updatePaginationUI(this.paginationState, this.paginationElements);
    }

    /**
     * Load devices data from vector_db
     */
    public async loadDevicesData(): Promise<void> {
        console.log('Loading devices data from vector_db...');

        if (!this.canisterClient) {
            console.error('CanisterClient not available');
            return;
        }

        try {
            const result = await this.canisterClient.callCanister('vector_db', 'get_all_devices', []);
            console.log('Devices result:', result);

            if (result && Array.isArray(result)) {
                // Convert tuple format to device objects
                // Metadata comes as array of key-value tuples: [["key", "value"], ...]
                const devices = result.map((tuple: any) => {
                    const [sysId, metadataArray] = tuple;
                    const device: Device = { sys_id: sysId };

                    // Convert metadata array of tuples to object properties
                    if (Array.isArray(metadataArray)) {
                        metadataArray.forEach((kvPair: any) => {
                            if (Array.isArray(kvPair) && kvPair.length >= 2) {
                                const [key, value] = kvPair;
                                device[key] = value;
                            }
                        });
                    }

                    return device;
                });
                console.log(`Parsed ${devices.length} devices`);

                this.allDevices = devices;
                this.filteredDevices = [...devices];
                this.updateDevicesPageStats(devices);

                // Sprint 29: Reset pagination with new total and render
                this.paginationState = resetPagination(this.paginationState, devices.length);
                this.renderCurrentPage();
            } else if (result && result.raw) {
                const devices = this.parseDevicesFromCandid(result.raw);
                console.log(`Parsed ${devices.length} devices (from raw)`);

                this.allDevices = devices;
                this.filteredDevices = [...devices];
                this.updateDevicesPageStats(devices);

                // Sprint 29: Reset pagination with new total and render
                this.paginationState = resetPagination(this.paginationState, devices.length);
                this.renderCurrentPage();
            }
        } catch (error) {
            console.error('Error loading devices:', error);
        }
    }

    /**
     * Parse devices from Candid string format
     */
    private parseDevicesFromCandid(raw: string): Device[] {
        const devices: Device[] = [];

        let i = 0;
        while (i < raw.length) {
            const recordStart = raw.indexOf('record {', i);
            if (recordStart === -1) break;

            const sysIdMatch = raw.substring(recordStart).match(/record\s*\{\s*"([^"]+)"/);
            if (!sysIdMatch) {
                i = recordStart + 1;
                continue;
            }
            const sysId = sysIdMatch[1];

            const vecStart = raw.indexOf('vec {', recordStart);
            if (vecStart === -1) break;

            let braceCount = 0;
            let vecContentStart = vecStart + 5;
            let vecEnd = -1;

            for (let j = vecStart; j < raw.length; j++) {
                if (raw[j] === '{') braceCount++;
                if (raw[j] === '}') {
                    braceCount--;
                    if (braceCount === 0) {
                        vecEnd = j;
                        break;
                    }
                }
            }

            if (vecEnd === -1) break;

            const metadataStr = raw.substring(vecContentStart, vecEnd);
            i = vecEnd + 1;

            const device: Device = { sys_id: sysId };

            const kvRegex = /record\s*\{\s*"([^"]+)";\s*"([^"]*)"\s*\};/g;
            let kvMatch;
            while ((kvMatch = kvRegex.exec(metadataStr)) !== null) {
                device[kvMatch[1]] = kvMatch[2];
            }

            devices.push(device);
        }

        return devices;
    }

    /**
     * Update device statistics on the page
     */
    private updateDevicesPageStats(devices: Device[]): void {
        const total = devices.length;
        const online = devices.filter(d => d.status === 'Online').length;
        const offline = devices.filter(d => d.status === 'Offline').length;
        const unknown = devices.filter(d => !d.status || d.status === 'Unknown').length;

        this.updateElementText('devices-page-total-count', total);
        this.updateElementText('devices-page-online-count', online);
        this.updateElementText('devices-page-offline-count', offline);
        this.updateElementText('unknown-devices-count', unknown);

        console.log(`Updated device stats: ${total} total, ${online} online, ${offline} offline, ${unknown} unknown`);
    }

    /**
     * Render the devices table with given devices
     * Sprint 29: Renamed from populateDevicesTable to support pagination
     */
    private renderDevicesTable(devices: Device[]): void {
        const tbody = document.getElementById('devices-table-body');
        if (!tbody) {
            console.error('Devices table body not found');
            return;
        }

        tbody.innerHTML = '';

        if (devices.length === 0) {
            tbody.innerHTML = '<tr><td colspan="7" class="text-center py-4 text-gray-500">No devices found</td></tr>';
            return;
        }

        devices.forEach(device => {
            const row = document.createElement('tr');
            row.className = 'border-t hover:bg-gray-50';

            const statusClass = device.status === 'Online' ? 'bg-green-100 text-green-800' :
                               device.status === 'Offline' ? 'bg-red-100 text-red-800' :
                               'bg-gray-100 text-gray-800';

            row.innerHTML = `
                <td class="py-3 px-4">${this.escapeHtml(device.hostname || device.device_id || 'Unknown')}</td>
                <td class="py-3 px-4">${this.escapeHtml(device.device_type || 'Unknown')}</td>
                <td class="py-3 px-4">${this.escapeHtml(device.ip || device.device_id || 'N/A')}</td>
                <td class="py-3 px-4">${this.escapeHtml(device.os || 'Unknown')}</td>
                <td class="py-3 px-4">${this.escapeHtml(device.os_version || 'N/A')}</td>
                <td class="py-3 px-4">
                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${statusClass}">
                        ${this.escapeHtml(device.status || 'Unknown')}
                    </span>
                </td>
                <td class="py-3 px-4">
                    <button class="text-blue-600 hover:text-blue-800 text-sm" onclick="viewDevice('${device.sys_id}')">
                        View
                    </button>
                </td>
            `;

            tbody.appendChild(row);
        });

        console.log(`Populated devices table with ${devices.length} rows`);
    }

    /**
     * Setup device search and filter handlers
     */
    private setupDeviceSearchAndFilters(): void {
        console.log('Setting up device search and filters...');

        const searchInput = document.getElementById('device-search') as HTMLInputElement;
        const typeFilter = document.getElementById('device-type-filter') as HTMLSelectElement;
        const osFilter = document.getElementById('device-os-filter') as HTMLSelectElement;
        const applyButton = document.getElementById('apply-device-filters');
        const resetButton = document.getElementById('reset-device-filters');

        if (!searchInput || !typeFilter || !osFilter || !applyButton || !resetButton) {
            console.warn('Some device filter elements not found');
            return;
        }

        searchInput.addEventListener('input', () => this.applyDeviceFilters());
        applyButton.addEventListener('click', () => this.applyDeviceFilters());
        resetButton.addEventListener('click', () => {
            searchInput.value = '';
            typeFilter.value = '';
            osFilter.value = '';
            this.applyDeviceFilters();
        });

        console.log('Device search and filters setup complete');
    }

    /**
     * Apply device filters
     * Sprint 29: Updated to use pagination
     */
    private applyDeviceFilters(): void {
        const searchInput = document.getElementById('device-search') as HTMLInputElement;
        const typeFilter = document.getElementById('device-type-filter') as HTMLSelectElement;
        const osFilter = document.getElementById('device-os-filter') as HTMLSelectElement;

        if (!searchInput || !typeFilter || !osFilter) {
            return;
        }

        const searchTerm = searchInput.value.toLowerCase().trim();
        const selectedType = typeFilter.value;
        const selectedOs = osFilter.value;

        let filtered = [...this.allDevices];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(device =>
                (device.hostname?.toLowerCase().includes(searchTerm)) ||
                (device.device_id?.toLowerCase().includes(searchTerm)) ||
                (device.ip?.toLowerCase().includes(searchTerm))
            );
        }

        // Apply type filter
        if (selectedType) {
            filtered = filtered.filter(device =>
                device.device_type?.toLowerCase() === selectedType.toLowerCase()
            );
        }

        // Apply OS filter
        if (selectedOs) {
            filtered = filtered.filter(device =>
                device.os?.toLowerCase().includes(selectedOs.toLowerCase())
            );
        }

        // Sprint 29: Update filtered devices and reset pagination
        this.filteredDevices = filtered;
        this.paginationState = resetPagination(this.paginationState, filtered.length);
        this.renderCurrentPage();
        console.log(`Filtered to ${filtered.length} devices`);
    }

    /**
     * Get all devices (for external access)
     */
    public getAllDevices(): Device[] {
        return this.allDevices;
    }
}
